import { checklistClient } from '../../config/api';
import {
  Question,
  QuestionApiResponse,
  QuestionsApiResponse,
  AddQuestionRequest,
  UpdateQuestionRequest,
} from '../types/checklist.types';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';

export class QuestionService {
  private readonly basePath = '/api/sections';

  getAllQuestions = async (sectionId: string): Promise<Question[]> => {
    try {
      const response = await checklistClient.get<QuestionsApiResponse>(
        `${this.basePath}/${sectionId}/questions`
      );

      if (!response.data || !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error fetching questions');
      }

      return response.data.data || [];
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching questions');
    }
  };

  getQuestionById = async (sectionId: string, questionId: string): Promise<Question> => {
    try {
      const response = await checklistClient.get<QuestionApiResponse>(
        `${this.basePath}/${sectionId}/questions/${questionId}`
      );

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error fetching question');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching question');
    }
  };

  addQuestion = async (sectionId: string, data: AddQuestionRequest): Promise<Question> => {
    try {
      const response = await checklistClient.post<QuestionApiResponse>(
        `${this.basePath}/${sectionId}/questions`,
        data
      );

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error adding question');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error adding question');
    }
  };

  updateQuestion = async (
    sectionId: string,
    questionId: string,
    data: UpdateQuestionRequest
  ): Promise<Question> => {
    try {
      const response = await checklistClient.put<QuestionApiResponse>(
        `${this.basePath}/${sectionId}/questions/${questionId}`,
        data
      );

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error updating question');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error updating question');
    }
  };

  deleteQuestion = async (sectionId: string, questionId: string): Promise<void> => {
    try {
      const response = await checklistClient.delete<QuestionApiResponse>(
        `${this.basePath}/${sectionId}/questions/${questionId}`
      );

      if (response.data && !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error deleting question');
      }
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error deleting question');
    }
  };
}

export const questionService = new QuestionService();
